import { useState } from "react";
import PropTypes from "prop-types";
import Switch from "@mui/material/Switch";
import Navbar from "layouts/ecommerce/posnavbar/Navbar";
import { PosCustomButton } from "../../../../customComponents/PosCustomButton/PosCustomButton";
import PosCustomDropdown from "../../../../customComponents/PosCustomDropdown/PosCustomDropdown";
import CustomPopupModal from "../../../../customComponents/CustomPopupModal/CustomPopupModal";
import {
  ClockIcon,
  Cog6ToothIcon,
  TruckIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  BuildingOffice2Icon,
  Squares2X2Icon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import "../index.css";
import "./settings-kitchenOS.css";
import { MOCK_INITIAL_SLOTS, MOCK_DEFAULT_SETTINGS } from "../MOCK_DATA_KOS";

// ─── Static Config ────────────────────────────────────────────────────────────

const DISPATCH_MODE_OPTIONS = [
  { label: "Direct Sale (Auto-SO)", value: "direct-sale" },
  { label: "Stock Transfer", value: "stock-transfer" },
  { label: "Consignment (Delayed Sales)", value: "consignment" },
  { label: "Periodic SO", value: "periodic-so" },
  { label: "PO-Linked Dispatch", value: "po-linked" },
];

const BILLING_CYCLE_OPTIONS = [
  { label: "Daily Consolidation", value: "daily" },
  { label: "Weekly Consolidation", value: "weekly" },
  { label: "Monthly Consolidation", value: "monthly" },
];

const LOOKBACK_WINDOW_OPTIONS = [
  { label: "2 Weeks", value: "2w" },
  { label: "4 Weeks (Standard)", value: "4w" },
  { label: "6 Weeks", value: "6w" },
  { label: "8 Weeks", value: "8w" },
];

const WASTAGE_OPTIONS = [
  { label: "Kitchen Org (Default)", value: "kitchen" },
  { label: "Outlet (Requesting Store)", value: "outlet" },
  { label: "Split Equally", value: "split" },
];

const QC_OPTIONS = [
  { label: "Kitchen Org (Default)", value: "kitchen" },
  { label: "Outlet (Requesting Store)", value: "outlet" },
  { label: "Split Equally", value: "split" },
];

const PRICING_TIER_OPTIONS = [
  { label: "Tier 1 (+10% Margin)", value: "tier1" },
  { label: "Tier 2 (+15% Margin)", value: "tier2" },
  { label: "Tier 3 (+20% Margin)", value: "tier3" },
];

const CATEGORY_OPTIONS = [
  { label: "Dough & Sauces", value: "dough-sauces" },
  { label: "Salads & Proteins", value: "salads-proteins" },
  { label: "Beverages", value: "beverages" },
  { label: "Desserts", value: "desserts" },
  { label: "All Categories", value: "all" },
];

const TABS = ["Org Configuration", "Preparation Slots"];

// ─── Section Header ───────────────────────────────────────────────────────────

function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="s_kos_section-header">
      <div className="s_kos_section-icon">
        <Icon />
      </div>
      <div>
        <p className="s_kos_section-title">{title}</p>
        <p className="s_kos_section-sub">{subtitle}</p>
      </div>
    </div>
  );
}

SectionHeader.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
};

// ─── Field ────────────────────────────────────────────────────────────────────

function Field({ label, children }) {
  return (
    <div className="s_kos_field">
      <label className="s_kos_label">{label}</label>
      {children}
    </div>
  );
}

Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

// ─── Add Slot Modal ───────────────────────────────────────────────────────────

function AddSlotModal({ open, onClose, onAdd }) {
  const [title, setTitle] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [category, setCategory] = useState({});

  function handleClose() {
    setTitle("");
    setStartTime("");
    setEndTime("");
    setCategory({});
    onClose();
  }

  function handleCreate() {
    if (!title.trim()) return;
    onAdd({
      id: Date.now(),
      title: title.trim(),
      startTime,
      endTime,
      category: category.label || "All Categories",
    });
    handleClose();
  }

  return (
    <CustomPopupModal
      open={open}
      onClose={handleClose}
      title="Create Prep Slot"
      width="480px"
      footer={
        <div className="s_kos_modal-footer">
          <PosCustomButton
            title="Cancel"
            variant="black-s"
            p="8px 20px"
            fontSize="13px"
            onClickFunction={handleClose}
          />
          <PosCustomButton
            title="Create Slot"
            variant="blue-d"
            p="8px 20px"
            fontSize="13px"
            onClickFunction={handleCreate}
          />
        </div>
      }
    >
      <p className="s_kos_modal-sub">Configure the time window and mapping for this prep slot.</p>

      <div className="s_kos_modal-fields">
        <Field label="Slot Title">
          <input
            type="text"
            className="s_kos_input"
            placeholder="e.g. Morning Prep"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>

        <div className="s_kos_two-col">
          <Field label="Start Time">
            <input
              type="time"
              className="s_kos_input"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </Field>
          <Field label="Target End Time">
            <input
              type="time"
              className="s_kos_input"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </Field>
        </div>

        <Field label="Mapped Category / Item">
          <PosCustomDropdown
            dropdownData={CATEGORY_OPTIONS}
            placeholder="Select category to map"
            selectedData={category}
            setSelectedData={setCategory}
            width="100%"
            enableScrollEffect={false}
          />
        </Field>
      </div>
    </CustomPopupModal>
  );
}

AddSlotModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
};

// ─── Org Configuration Tab ────────────────────────────────────────────────────

const DD_IDS = ["dispatchMode", "billingCycle", "lookbackWindow", "wastageCost", "qcCost", "downtownTier", "airportTier"];
const INITIAL_DD_KEYS = Object.fromEntries(DD_IDS?.map((id) => [id, 0]));

function OrgConfigTab({ settings, onSettingChange }) {
  const [ddKeys, setDdKeys] = useState(INITIAL_DD_KEYS);

  function focusDropdown(id) {
    setDdKeys((prev) => {
      const next = {};
      DD_IDS?.forEach((k) => { next[k] = k === id ? prev[k] : prev[k] + 1; });
      return next;
    });
  }

  function dd(id, dropdownData, placeholder, settingKey, openUpward = false) {
    return (
      <div onMouseDown={() => focusDropdown(id)}>
        <PosCustomDropdown
          key={ddKeys[id]}
          dropdownData={dropdownData}
          placeholder={placeholder}
          selectedData={settings[settingKey]}
          setSelectedData={(val) => onSettingChange(settingKey, val)}
          width="100%"
          enableScrollEffect={false}
          openUpward={openUpward}
        />
      </div>
    );
  }

  return (
    <div className="s_kos_sections">
      {/* Default Dispatch Settings */}
      <div className="s_kos_section">
        <SectionHeader
          icon={TruckIcon}
          title="Default Dispatch Settings"
          subtitle="Configure how goods move and are billed across the org."
        />
        <div className="s_kos_section-body">
          <div className="s_kos_two-col">
            <Field label="Default Dispatch Mode">
              {dd("dispatchMode", DISPATCH_MODE_OPTIONS, "Select dispatch mode", "dispatchMode")}
            </Field>
            <Field label="Default Billing Cycle (Periodic SO)">
              {dd("billingCycle", BILLING_CYCLE_OPTIONS, "Select billing cycle", "billingCycle")}
            </Field>
          </div>
          <p className="s_kos_note">This can be overridden per outlet-kitchen pair.</p>
        </div>
      </div>

      {/* Forecast & Approvals */}
      <div className="s_kos_section">
        <SectionHeader
          icon={ChartBarIcon}
          title="Forecast & Approvals"
          subtitle="Set forecasting parameters and approval workflow rules."
        />
        <div className="s_kos_section-body">
          <div className="s_kos_two-col">
            <Field label="Demand Forecast Lookback Window">
              {dd("lookbackWindow", LOOKBACK_WINDOW_OPTIONS, "Select lookback window", "lookbackWindow")}
            </Field>
            <div className="s_kos_toggle-row">
              <div>
                <p className="s_kos_toggle-title">Two-Level Approval</p>
                <p className="s_kos_toggle-sub">Require L2 approval for large requests</p>
              </div>
              <Switch
                checked={settings.twoLevelApproval}
                onChange={(e) => onSettingChange("twoLevelApproval", e.target.checked)}
                color="primary"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Wastage & QC Levy Rules */}
      <div className="s_kos_section">
        <SectionHeader
          icon={CurrencyDollarIcon}
          title="Wastage & QC Levy Rules (Consignment)"
          subtitle="Determine which org absorbs costs during reconciliation variance."
        />
        <div className="s_kos_section-body">
          <div className="s_kos_two-col">
            <Field label="Wastage Cost Assigned To:">
              {dd("wastageCost", WASTAGE_OPTIONS, "Select assignment", "wastageCost")}
            </Field>
            <Field label="QC Rejection Cost Assigned To:">
              {dd("qcCost", QC_OPTIONS, "Select assignment", "qcCost")}
            </Field>
          </div>
        </div>
      </div>

      {/* Outlet-Specific Pricing & Billing */}
      <div className="s_kos_section">
        <SectionHeader
          icon={BuildingOffice2Icon}
          title="Outlet-Specific Pricing & Billing"
          subtitle="Configure pricing tiers and billing rules per outlet."
        />
        <div className="s_kos_section-body">
          <div className="s_kos_two-col">
            <Field label="Downtown Store Pricing Tier">
              {dd("downtownTier", PRICING_TIER_OPTIONS, "Select tier", "downtownTier", true)}
            </Field>
            <Field label="Airport Kiosk Pricing Tier">
              {dd("airportTier", PRICING_TIER_OPTIONS, "Select tier", "airportTier", true)}
            </Field>
          </div>
        </div>
      </div>
    </div>
  );
}

OrgConfigTab.propTypes = {
  settings: PropTypes.object.isRequired,
  onSettingChange: PropTypes.func.isRequired,
};

// ─── Preparation Slots Tab ────────────────────────────────────────────────────

function PreparationSlotsTab({ slots, onAddSlot }) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <div className="s_kos_section">
        <div className="s_kos_slots-header">
          <SectionHeader
            icon={ClockIcon}
            title="Preparation Slots"
            subtitle="Define time windows for prep tasks and map them to categories or items."
          />
          <PosCustomButton
            title="+ Add Slot"
            variant="blue-d"
            p="8px 16px"
            fontSize="13px"
            onClickFunction={() => setIsModalOpen(true)}
          />
        </div>

        <div className="s_kos_slot-list">
          {slots?.map((slot) => (
            <div key={slot.id} className="s_kos_slot-item">
              <div className="s_kos_slot-icon-wrap">
                <ClockIcon />
              </div>
              <div className="s_kos_slot-info">
                <p className="s_kos_slot-name">{slot.title}</p>
                <div className="s_kos_slot-meta">
                  <span className="s_kos_slot-time">
                    {slot.startTime} - {slot.endTime}
                  </span>
                  <span className="s_kos_slot-category">
                    <Squares2X2Icon />
                    {slot.category}
                  </span>
                </div>
              </div>
              <div className="s_kos_slot-actions">
                <button className="s_kos_slot-action-btn" title="Edit slot">
                  <PencilIcon />
                </button>
                <button className="s_kos_slot-action-btn s_kos_slot-action-delete" title="Delete slot">
                  <TrashIcon />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <AddSlotModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={(newSlot) => {
          onAddSlot(newSlot);
          setIsModalOpen(false);
        }}
      />
    </>
  );
}

PreparationSlotsTab.propTypes = {
  slots: PropTypes.array.isRequired,
  onAddSlot: PropTypes.func.isRequired,
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SettingsKitchenOS() {
  const [activeTab, setActiveTab] = useState("Org Configuration");
  const [slots, setSlots] = useState(MOCK_INITIAL_SLOTS);
  const [settings, setSettings] = useState(MOCK_DEFAULT_SETTINGS);

  function handleSettingChange(key, value) {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }

  function handleAddSlot(newSlot) {
    setSlots((prev) => [...prev, newSlot]);
  }

  return (
    <Navbar pageTitle="Kitchen OS">
      <div className="idx_kos_wrapper">
        <div className="s_kos_page">
          {/* Header */}
          <div className="s_kos_header">
            <div>
              <div className="s_kos_title-row">
                <Cog6ToothIcon className="s_kos_header-icon" />
                <h1 className="s_kos_title">Settings</h1>
              </div>
              <p className="s_kos_subtitle">Configure org-level policies and kitchen parameters.</p>
            </div>
            <PosCustomButton
              title="Save Changes"
              variant="blue-d"
              p="9px 20px"
              fontSize="14px"
            />
          </div>

          {/* Tabs */}
          <div className="s_kos_tabs">
            {TABS?.map((tab) => (
              <button
                key={tab}
                className={`s_kos_tab${activeTab === tab ? " s_kos_tab-active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === "Org Configuration" ? (
            <OrgConfigTab settings={settings} onSettingChange={handleSettingChange} />
          ) : (
            <PreparationSlotsTab slots={slots} onAddSlot={handleAddSlot} />
          )}
        </div>
      </div>
    </Navbar>
  );
}
